import { PortableText } from "next-sanity";
import { PortableTextBlock } from "sanity";
import { GroqImage } from "@/helpers/Image";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import { GROQButton, GROQImage } from "@/sanity/lib/definitions";
import { CTAButton } from "@/shadcn/ui/button";
import PhoneMockup from "@/components/global/ui/PhoneMockup";

interface WhatsappQrSectionProps {
  data: {
    whatsappQrTitle?: PortableTextBlock[];
    whatsappQrText?: string;
    whatsappQrButton?: GROQButton;
    whatsappQrCode?: GROQImage;
  };
}

function ScanIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="h-full w-full"
      aria-hidden="true">
      <path
        d="M16.5 17.9999H7.5C7.10218 17.9999 6.72064 17.8418 6.43934 17.5605C6.15804 17.2792 6 16.8977 6 16.4999V14.2499H7.5V16.4999H16.5V14.2499H18V16.4999C18 16.8977 17.842 17.2792 17.5607 17.5605C17.2794 17.8418 16.8978 17.9999 16.5 17.9999Z"
        fill="currentColor"
      />
      <path
        d="M22.5 11.2498H1.5V12.7499H22.5V11.2498Z"
        fill="currentColor"
      />
      <path
        d="M18 9.74985H16.5V7.49985H7.5V9.74985H6V7.49985C6 7.10203 6.15804 6.72049 6.43934 6.43919C6.72064 6.15789 7.10218 5.99985 7.5 5.99985H16.5C16.8978 5.99985 17.2794 6.15789 17.5607 6.43919C17.842 6.72049 18 7.10203 18 7.49985V9.74985Z"
        fill="currentColor"
      />
      <path
        d="M22.5 7.5H21V3H16.5V1.5H22.5V7.5Z"
        fill="currentColor"
      />
      <path
        d="M3 7.5H1.5V1.5H7.5V3H3V7.5Z"
        fill="currentColor"
      />
      <path
        d="M7.5 22.5H1.5V16.5H3V21H7.5V22.5Z"
        fill="currentColor"
      />
      <path
        d="M22.5 22.5H16.5V21H21V16.5H22.5V22.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

function IconBadge() {
  return (
    <div className="relative h-[7.5rem] w-[7.5rem] shrink-0">
      <div className="absolute inset-0 rounded-[0.25rem] border border-[rgba(0,27,20,0.1)]" />
      <div className="absolute top-[0.875rem] left-[0.875rem] h-[5.75rem] w-[5.75rem] rounded-[0.25rem] bg-[rgba(26,168,127,0.1)]" />
      <div className="absolute top-[2.25rem] left-[2.25rem] h-[3rem] w-[3rem] text-[#149972]">
        <ScanIcon />
      </div>
    </div>
  );
}

const titleComponents = customRichTextComponent({
  block: {
    normal: ({ children }) => <TitleLine>{children}</TitleLine>,
    h2: ({ children }) => <TitleLine>{children}</TitleLine>,
    h3: ({ children }) => <TitleLine>{children}</TitleLine>,
  },
  marks: {
    // O Figma troca o tratamento entre os breakpoints: no desktop a primeira
    // metade é a serifada itálica, no mobile é a segunda (em verde). As marcas
    // só identificam as metades — quem decide a aparência é o breakpoint.
    em: ({ children }) => (
      <em className="font-geist font-normal not-italic lg:pr-[.125em] lg:font-blacker lg:italic">{children}</em>
    ),
    serif: ({ children }) => (
      <span className="font-blacker text-[#003828] italic lg:text-black lg:not-italic">{children}</span>
    ),
  },
});

function TitleLine({ children }: { children?: React.ReactNode }) {
  return (
    <h2 className="mb-0 font-geist text-[4rem] leading-[1.15] font-normal tracking-[-0.2rem] text-black lg:text-[6rem] lg:tracking-[-0.3rem]">
      {children}
    </h2>
  );
}

export default function WhatsappQrSection({ data }: WhatsappQrSectionProps) {
  if (!data.whatsappQrTitle && !data.whatsappQrCode) return null;

  return (
    <section className="flex w-full justify-center bg-white px-[2rem] pt-[8rem] lg:px-0 lg:pt-[12rem]">
      <div className="relative flex h-[94rem] w-[min(150rem,100%)] flex-col items-center overflow-hidden rounded-[0.5rem] border border-black/[0.05] bg-[#f9f9f9] px-[2rem] py-[6rem] lg:h-[56.125rem] lg:flex-row lg:items-center lg:px-[7.5rem] lg:py-[2.75rem]">
        <div className="flex w-full max-w-[49.75rem] flex-col items-center gap-[2rem] text-center lg:max-w-[53.5rem] lg:items-start lg:text-left">
          <IconBadge />

          {data.whatsappQrTitle && (
            <div className="max-w-[31rem] lg:max-w-[46.5rem]">
              <PortableText
                value={data.whatsappQrTitle}
                components={titleComponents}
              />
            </div>
          )}

          {data.whatsappQrText && (
            <p className="mb-0 max-w-[41.5rem] font-geist text-[2.25rem] leading-[1.5] font-normal tracking-[-0.1125rem] text-black/60 lg:max-w-[53.5rem] lg:text-[2.75rem] lg:tracking-[-0.1375rem]">
              {data.whatsappQrText}
            </p>
          )}

          {data.whatsappQrButton && (
            <CTAButton
              data={data.whatsappQrButton}
              className="w-full sm:w-full lg:!w-auto lg:min-w-[25.75rem] [&_.button]:!rounded-[0.25rem]"
            />
          )}
        </div>

        <PhoneMockup className="absolute top-[45.08rem] left-1/2 -translate-x-1/2 [--pw:43.42rem] lg:top-[4.21rem] lg:right-[13.2rem] lg:left-auto lg:translate-x-0 lg:[--pw:49.44rem]">
          {data.whatsappQrCode && (
            <div className="absolute top-[calc(var(--pw)*0.31668)] left-[calc(var(--pw)*0.12284)] h-[calc(var(--pw)*0.66656)] w-[calc(var(--pw)*0.66656)] overflow-hidden lg:top-[calc(var(--pw)*0.22427)]">
              <GroqImage
                src={data.whatsappQrCode.src}
                alt={data.whatsappQrCode.name || ""}
                width={data.whatsappQrCode.width}
                height={data.whatsappQrCode.height}
                style={{ width: "100%", height: "100%" }}
                className="object-contain"
              />
              <div className="pointer-events-none absolute inset-0 overflow-hidden motion-reduce:hidden">
                <div className="h-1/4 w-full animate-qr-scan border-b-[0.25rem] border-[#5ef0c0] bg-gradient-to-b from-transparent to-[#1aa87f]/50" />
              </div>
            </div>
          )}
        </PhoneMockup>
      </div>
    </section>
  );
}
